/**
 * Form primitives (after shadcn/ui's Field): a vertical group of label,
 * control, description and error. Deliberately no form library — a form's
 * state belongs to the component that owns it; these only lay it out and make
 * the error announce itself (`role="alert"`).
 *
 *     <Field invalid={error !== null}>
 *       <Label htmlFor="name">Name</Label>
 *       <Input id="name" aria-invalid={error !== null} aria-describedby="name-error" … />
 *       <FieldError id="name-error">{error}</FieldError>
 *     </Field>
 */
import type { ComponentProps } from 'react';
import { cn } from '../../lib/utils.js';

export function Field({ className, invalid, ...props }: ComponentProps<'div'> & { invalid?: boolean }) {
  return <div data-slot="field" data-invalid={invalid === true ? 'true' : undefined} className={cn('group grid gap-2', className)} {...props} />;
}

export function FieldDescription({ className, ...props }: ComponentProps<'p'>) {
  return <p data-slot="field-description" className={cn('text-sm text-muted-foreground', className)} {...props} />;
}

/** Renders nothing without children, so `{error}` can be passed as it is. */
export function FieldError({ className, children, ...props }: ComponentProps<'p'>) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <p data-slot="field-error" role="alert" className={cn('text-sm font-medium text-destructive', className)} {...props}>
      {children}
    </p>
  );
}
